import { BeconUserTypes } from "@becon-team/becon-fe-lib/my-types";
import {
  getUserAgeGroupText,
  getUserGenderText,
  getUserHairColorText,
  getUserHairDamageLevelText,
  getUserHairLossFamilyHistoryText,
  getUserHairTypeText,
  getUserScalpIssueData,
  getUserSkinColorText,
  isNonNullish,
} from "@becon-team/becon-fe-lib/utils";
import {
  validateZodSchemaFromJSON,
  zCreateMaxArrayLengthRule,
  zCreateMinArrayLengthRule,
  zCreateMustBeExistRule,
  zCreateNumericOnlyRule,
  zCreateRequiredRule,
  zValidateJson,
} from "@becon-team/becon-fe-lib/my-zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChangeEvent, FormEvent, ReactNode, useEffect, useState } from "react";
import { ChangeHandler, UseFormRegisterReturn, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { checkHideRaceInquiry } from "../utils/checkHideRaceInquiry";
import { useAppDispatch } from "store/redux/modules";
import { getUsers } from "store/redux/modules/user";
import { queryClient } from "libs/react-query/queryClient";
import queryKeys from "libs/react-query/query_key";

import {
  YesOrNoEnum,
  NoToMoreThenOneEnum,
  NoToMoreThenThreeEnum,
  SleepTimeEnum,
  StressLevelEnum,
  PreferenceHairEnum,
  PreferenceShampooEnum,
  NoneToMoreThenThreeEnum,
  WashHairCycleEnum,
} from "../services/user.dto";

import {
  useGetUserHealthInquiry,
  useGetUserInquiry,
  usePostUserHealthInquiry,
  usePutUserInquiry,
} from "../services/user.query";
import {
  getCoffeeOptionText,
  getDietOptionText,
  getDrinkingOptionText,
  getSleepTimeOptionText,
  getSmokingOptionText,
  getStressLevelOptionText,
  getWorkOutOptionText,
  getUserPreferenceShampooOptionText,
  getUserPreferenceHairOptionText,
  getUserLifestyleWashHairCycleOptionText,
  getUserLifestyleTreatmentProductOptionText,
  getUserLifestylePermOptionText,
  getUserLifestyleDyeingOptionText,
  getUserHairLossTypeAndLevelData,
} from "../utils/(getUserData)";
import { useGetAccountInfo } from "api/@auth/query_auth";

import type { Undefinable } from "@becon-team/becon-fe-lib/my-types";
import { HairTypeEnum } from "modules/account/services/account.dto";

type OptionSelect = {
  value: string;
  display: string;
};

type OptionCheckbox = {
  value: string;
  display: ReactNode;
};

const HairlossTypeAndLevelCamelSchema = z.object({
  hairlossType: z.nativeEnum(BeconUserTypes.HairlossTypeEnum),
  hairlossLevel: z.number(),
});

interface UseFormPutUserInquiry {
  (args: { userId: Undefinable<string>; onSuccess?: () => void }): {
    genderOptions: OptionCheckbox[];
    ageGroupOptions: OptionSelect[];
    hairlossTypeAndLevelOptions: OptionCheckbox[];
    hairLossFamilyHistoryOptions: OptionCheckbox[];
    scalpIssuesOptions: OptionCheckbox[];
    hairDamageLevelOptions: OptionCheckbox[];
    skinColorOptions: OptionCheckbox[];
    hairColorOptions: OptionCheckbox[];
    hairTypeOptions: OptionCheckbox[];
    dietOptions: OptionCheckbox[];
    smokingOptions: OptionCheckbox[];
    coffeeOptions: OptionCheckbox[];
    drinkingOptions: OptionCheckbox[];
    workOutOptions: OptionCheckbox[];
    sleepTimeOptions: OptionCheckbox[];
    stressLevelOptions: OptionCheckbox[];
    preferenceShampooOptions: OptionCheckbox[];
    preferenceHairOptions: OptionCheckbox[];
    dyeingOptions: OptionCheckbox[];
    permOptions: OptionCheckbox[];
    washHairCycleOptions: OptionCheckbox[];
    treatmentProductOptions: OptionCheckbox[];
    genderRegister: UseFormRegisterReturn<"gender">;
    ageGroupRegister: UseFormRegisterReturn<"ageGroup">;
    hairlossTypeAndLevelRegister: UseFormRegisterReturn<"hairlossTypeAndLevel">;
    hairlossFamilyHistoryRegister: UseFormRegisterReturn<"hairlossFamilyHistory">;
    scalpIssuesRegister: UseFormRegisterReturn<"scalpIssues">;
    hairDamageLevelRegister: UseFormRegisterReturn<"hairDamageLevel">;
    skinColorRegister: UseFormRegisterReturn<"skinColor">;
    hairColorRegister: UseFormRegisterReturn<"hairColor">;
    hairTypeRegister: UseFormRegisterReturn<"hairType">;
    dietRegister: UseFormRegisterReturn<"diet">;
    smokingRegister: UseFormRegisterReturn<"smoking">;
    coffeeRegister: UseFormRegisterReturn<"coffee">;
    drinkingRegister: UseFormRegisterReturn<"drinking">;
    workOutRegister: UseFormRegisterReturn<"workOut">;
    sleepTimeRegister: UseFormRegisterReturn<"sleepTime">;
    stressLevelRegister: UseFormRegisterReturn<"stressLevel">;
    preferenceShampooRegister: UseFormRegisterReturn<"preferenceShampoo">;
    preferenceHairRegister: UseFormRegisterReturn<"preferenceHair">;
    dyeingRegister: UseFormRegisterReturn<"dyeing">;
    permRegister: UseFormRegisterReturn<"perm">;
    washHairCycleRegister: UseFormRegisterReturn<"washHairCycle">;
    treatmentProductRegister: UseFormRegisterReturn<"treatmentProduct">;
    errorMessages: Record<
      | "gender"
      | "ageGroup"
      | "hairlossTypeAndLevel"
      | "hairlossFamilyHistory"
      | "scalpIssues"
      | "hairDamageLevel"
      | "skinColor"
      | "hairColor"
      | "hairType"
      | "diet"
      | "smoking"
      | "coffee"
      | "drinking"
      | "workOut"
      | "sleepTime"
      | "stressLevel"
      | "preferenceShampoo"
      | "preferenceHair"
      | "dyeing"
      | "perm"
      | "washHairCycle"
      | "treatmentProduct",
      string | undefined
    >;
    onSubmit: (event: FormEvent) => void;
    isSubmitting: boolean;
    isFormChanged: boolean;
  };
}

export const useFormPutUserInquiry: UseFormPutUserInquiry = ({ userId, onSuccess }) => {
  const { t } = useTranslation();

  const FormSchema = z.object({
    gender: zCreateMustBeExistRule(t, t("성별")).and(z.nativeEnum(BeconUserTypes.UserGenderEnum).nullable().optional()),
    // AgeGroup에 명시적인 선택 안함이 없어서, z.literal("")과 !ageGroup(in submit) 및 disabled(in option tag) 처리 필요
    ageGroup: zCreateRequiredRule(t, t("나이대")).and(z.nativeEnum(BeconUserTypes.AgeGroupEnum).or(z.literal(""))),
    hairlossTypeAndLevel: zCreateMustBeExistRule(t, t("탈모 상태")).and(zValidateJson(HairlossTypeAndLevelCamelSchema)),
    hairlossFamilyHistory: zCreateMustBeExistRule(t, t("탈모 가족력")).and(
      z.nativeEnum(BeconUserTypes.HairlossFamilyHistoryEnum),
    ),
    scalpIssues: zCreateMinArrayLengthRule(t, t("두피 고민"), 1, z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum)).and(
      zCreateMaxArrayLengthRule(t, t("두피 고민"), 3, z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum)),
    ),
    hairDamageLevel: zCreateMustBeExistRule(t, t("모발 손상도")).and(zCreateNumericOnlyRule(t, t("모발 손상도"))),
    skinColor: zCreateMustBeExistRule(t, t("피부톤")).and(
      z.nativeEnum(BeconUserTypes.SkinColorEnum).nullable().optional(),
    ),
    hairColor: zCreateMustBeExistRule(t, t("모발 색")).and(
      z.nativeEnum(BeconUserTypes.HairColorEnum).nullable().optional(),
    ),
    hairType: zCreateMustBeExistRule(t, t("모발 형태")).and(z.nativeEnum(HairTypeEnum).nullable().optional()),
    preferenceShampoo: zCreateMaxArrayLengthRule(t, t("선호 샴푸 특징"), 2, z.nativeEnum(PreferenceShampooEnum)),
    preferenceHair: z.nativeEnum(PreferenceHairEnum).nullable().optional(),
    dyeing: z.nativeEnum(NoneToMoreThenThreeEnum).nullable().optional(),
    perm: z.nativeEnum(NoneToMoreThenThreeEnum).nullable().optional(),
    washHairCycle: z.nativeEnum(WashHairCycleEnum).nullable().optional(),
    treatmentProduct: z.nativeEnum(YesOrNoEnum).nullable().optional(),
    diet: z.nativeEnum(YesOrNoEnum).nullable().optional(),
    smoking: z.nativeEnum(NoToMoreThenOneEnum).nullable().optional(),
    coffee: z.nativeEnum(NoToMoreThenThreeEnum).nullable().optional(),
    drinking: z.nativeEnum(NoToMoreThenThreeEnum).nullable().optional(),
    workOut: z.nativeEnum(NoToMoreThenThreeEnum).nullable().optional(),
    sleepTime: z.nativeEnum(SleepTimeEnum).nullable().optional(),
    stressLevel: z.nativeEnum(StressLevelEnum).nullable().optional(),
  });
  type Form = z.infer<typeof FormSchema>;
  type KeyOfForm = keyof Form;

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof FormSchema>>({
    mode: "onSubmit",
    resolver: zodResolver(FormSchema),
  });
  const gender = watch("gender");

  const genderOptions = Object.values(BeconUserTypes.UserGenderEnum).map((gender) => ({
    value: gender,
    display: getUserGenderText(t, gender),
  }));
  const ageGroupOptions = [
    {
      value: "",
      display: t("선택 안 함"),
    },
    ...(Object.values(BeconUserTypes.AgeGroupEnum)
      .filter((ageGroup) => ageGroup !== BeconUserTypes.AgeGroupEnum.OVER50)
      ?.map((ageGroup) => ({
        value: ageGroup,
        display: getUserAgeGroupText(t, ageGroup),
      })) || []),
  ];
  const hairlossTypeAndLevelOptions = [
    {
      value: JSON.stringify({
        hairlossType: BeconUserTypes.HairlossTypeEnum.NONE,
        hairlossLevel: 0,
      }),
      display: (
        <div className="flex flex-col items-center justify-center">
          <div className="[&>svg]:w-48ptv">{getUserHairLossTypeAndLevelData(t, "NONE", 0, gender).icon}</div>
          <div className="text-md">{getUserHairLossTypeAndLevelData(t, "NONE", 0, gender).text}</div>
        </div>
      ),
    },
    {
      value: JSON.stringify({
        hairlossType: BeconUserTypes.HairlossTypeEnum.THINNING_HAIR,
        hairlossLevel: 2,
      }),
      display: (
        <div className="flex flex-col items-center justify-center">
          <div className="[&>svg]:w-48ptv">{getUserHairLossTypeAndLevelData(t, "THINNING_HAIR", 2, gender).icon}</div>
          <div className="text-md">{getUserHairLossTypeAndLevelData(t, "THINNING_HAIR", 2, gender).text}</div>
        </div>
      ),
    },
    {
      value: JSON.stringify({
        hairlossType: BeconUserTypes.HairlossTypeEnum.GENERAL,
        hairlossLevel: 1,
      }),
      display: (
        <div className="flex flex-col items-center justify-center">
          <div className="[&>svg]:w-48ptv">{getUserHairLossTypeAndLevelData(t, "GENERAL", 1, gender).icon}</div>
          <div className="text-md">{getUserHairLossTypeAndLevelData(t, "GENERAL", 1, gender).text}</div>
        </div>
      ),
    },
    {
      value: JSON.stringify({
        hairlossType: BeconUserTypes.HairlossTypeEnum.GENERAL,
        hairlossLevel: 2,
      }),
      display: (
        <div className="flex flex-col items-center justify-center">
          <div className="[&>svg]:w-48ptv">{getUserHairLossTypeAndLevelData(t, "GENERAL", 2, gender).icon}</div>
          <div className="text-md">{getUserHairLossTypeAndLevelData(t, "GENERAL", 2, gender).text}</div>
        </div>
      ),
    },
    {
      value: JSON.stringify({
        hairlossType: BeconUserTypes.HairlossTypeEnum.GENERAL,
        hairlossLevel: 3,
      }),
      display: (
        <div className="flex flex-col items-center justify-center">
          <div className="[&>svg]:w-48ptv">{getUserHairLossTypeAndLevelData(t, "GENERAL", 3, gender).icon}</div>
          <div className="text-md">{getUserHairLossTypeAndLevelData(t, "GENERAL", 3, gender).text}</div>
        </div>
      ),
    },
  ];
  const hairLossFamilyHistoryOptions = Object.values(BeconUserTypes.HairlossFamilyHistoryEnum).map(
    (hairLossFamilyHistory) => ({
      value: hairLossFamilyHistory,
      display: getUserHairLossFamilyHistoryText(t, hairLossFamilyHistory),
    }),
  );
  const scalpIssuesOptions = Object.values(BeconUserTypes.ScalpIssueTypeEnum)
    .filter((scalpIssue) => scalpIssue !== BeconUserTypes.ScalpIssueTypeEnum.HAIRLOSS)
    .map((scalpIssue) => ({
      value: scalpIssue,
      display: (
        <div className="flex flex-col items-center justify-center">
          <div className="[&>svg]:w-48ptv">{getUserScalpIssueData(t, scalpIssue).icon}</div>
          <div className="text-md">{getUserScalpIssueData(t, scalpIssue).text}</div>
        </div>
      ),
    }));
  const hairDamageLevelOptions = [0, 1, 2, 3].map((level) => ({
    value: `${level}`,
    display: getUserHairDamageLevelText(t, level),
  }));
  const skinColorOptions = Object.values(BeconUserTypes.SkinColorEnum)
    .filter((skinColor) => skinColor !== BeconUserTypes.SkinColorEnum.UNKNOWN)
    .map((skinColor) => ({
      value: skinColor,
      display: <div className="text-4xl">{getUserSkinColorText(t, skinColor)}</div>,
    }));
  const hairColorOptions = Object.values(BeconUserTypes.HairColorEnum).map((hairColor) => ({
    value: hairColor,
    display: getUserHairColorText(t, hairColor),
  }));
  const hairTypeOptions = Object.values(BeconUserTypes.HairTypeEnum).map((hairType) => ({
    value: hairType,
    display: getUserHairTypeText(t, hairType),
  }));
  const preferenceShampooOptions = Object.values(PreferenceShampooEnum).map((option) => ({
    value: option,
    display: getUserPreferenceShampooOptionText(t, option),
  }));
  const preferenceHairOptions = Object.values(PreferenceHairEnum).map((option) => ({
    value: option,
    display: getUserPreferenceHairOptionText(t, option),
  }));
  const dyeingOptions = Object.values(NoneToMoreThenThreeEnum).map((option) => ({
    value: option,
    display: getUserLifestyleDyeingOptionText(t, option),
  }));
  const permOptions = Object.values(NoneToMoreThenThreeEnum).map((option) => ({
    value: option,
    display: getUserLifestylePermOptionText(t, option),
  }));
  const washHairCycleOptions = Object.values(WashHairCycleEnum).map((option) => ({
    value: option,
    display: getUserLifestyleWashHairCycleOptionText(t, option),
  }));
  const treatmentProductOptions = Object.values(YesOrNoEnum).map((option) => ({
    value: option,
    display: getUserLifestyleTreatmentProductOptionText(t, option),
  }));
  const dietOptions = Object.values(YesOrNoEnum).map((option) => ({
    value: option,
    display: getDietOptionText(t, option),
  }));
  const smokingOptions = Object.values(NoToMoreThenOneEnum).map((option) => ({
    value: option,
    display: getSmokingOptionText(t, option),
  }));
  const coffeeOptions = Object.values(NoToMoreThenThreeEnum).map((option) => ({
    value: option,
    display: getCoffeeOptionText(t, option),
  }));
  const drinkingOptions = Object.values(NoToMoreThenThreeEnum).map((option) => ({
    value: option,
    display: getDrinkingOptionText(t, option),
  }));
  const workOutOptions = Object.values(NoToMoreThenThreeEnum).map((option) => ({
    value: option,
    display: getWorkOutOptionText(t, option),
  }));
  const sleepTimeOptions = Object.values(SleepTimeEnum).map((option) => ({
    value: option,
    display: getSleepTimeOptionText(t, option),
  }));
  const stressLevelOptions = Object.values(StressLevelEnum).map((option) => ({
    value: option,
    display: getStressLevelOptionText(t, option),
  }));

  const errorMessages: Record<KeyOfForm, string | undefined> = Object.fromEntries(
    Object.entries(errors).map(([key, value]) => [key as KeyOfForm, value.message]),
  ) as Record<KeyOfForm, string | undefined>;

  const scalpIssues = watch("scalpIssues");
  const onChangeScalpIssues = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    if (value === BeconUserTypes.ScalpIssueTypeEnum.NONE && checked) {
      setValue("scalpIssues", [BeconUserTypes.ScalpIssueTypeEnum.NONE]);
    } else if (value === BeconUserTypes.ScalpIssueTypeEnum.NONE && !checked) {
      setValue("scalpIssues", []);
    } else if (checked) {
      if (scalpIssues.includes(BeconUserTypes.ScalpIssueTypeEnum.NONE)) {
        setValue("scalpIssues", [value as BeconUserTypes.ScalpIssueTypeEnum]);
      } else if (scalpIssues?.length < 3) {
        setValue("scalpIssues", [...scalpIssues, value as BeconUserTypes.ScalpIssueTypeEnum]);
      } else {
        setValue("scalpIssues", [...scalpIssues.slice(0, 2), value as BeconUserTypes.ScalpIssueTypeEnum]);
      }
    } else {
      setValue(
        "scalpIssues",
        scalpIssues.filter((issue) => issue !== value),
      );
    }
  };

  const preferenceShampoo = watch("preferenceShampoo");

  const onChangePreferenceShampoo = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    if (value === PreferenceShampooEnum.NONE && checked) {
      setValue("preferenceShampoo", [PreferenceShampooEnum.NONE]);
    } else if (value === PreferenceShampooEnum.NONE && !checked) {
      setValue("preferenceShampoo", []);
    } else if (checked) {
      if (preferenceShampoo.includes(PreferenceShampooEnum.NONE)) {
        setValue("preferenceShampoo", [value as PreferenceShampooEnum]);
      } else {
        setValue("preferenceShampoo", [...preferenceShampoo.slice(0, 1), value as PreferenceShampooEnum]);
      }
    } else {
      setValue(
        "preferenceShampoo",
        preferenceShampoo.filter((issue) => issue !== value),
      );
    }
  };

  useEffect(() => {
    if ((scalpIssues?.length || 0) < 1) {
      setValue("scalpIssues", [BeconUserTypes.ScalpIssueTypeEnum.NONE]);
    }
  }, [scalpIssues?.length, setValue]);

  const genderRegister = { ...register("gender"), type: "radio" };
  const ageGroupRegister = register("ageGroup");
  const hairlossTypeAndLevelRegister = { ...register("hairlossTypeAndLevel"), type: "radio" };
  const hairlossTypeAndLevel = watch("hairlossTypeAndLevel");
  const hairlossFamilyHistoryRegister = { ...register("hairlossFamilyHistory"), type: "radio" };
  const scalpIssuesRegister = {
    ...register("scalpIssues"),
    type: "checkbox",
    onChange: onChangeScalpIssues as ChangeHandler,
  };
  const hairDamageLevelRegister = { ...register("hairDamageLevel"), type: "radio" };
  const skinColorRegister = { ...register("skinColor"), type: "radio" };
  const hairColorRegister = { ...register("hairColor"), type: "radio" };
  const hairTypeRegister = { ...register("hairType"), type: "radio" };
  const preferenceShampooRegister = {
    ...register("preferenceShampoo"),
    type: "checkbox",
    onChange: onChangePreferenceShampoo as ChangeHandler,
  };
  const preferenceHairRegister = { ...register("preferenceHair"), type: "radio" };
  const dyeingRegister = { ...register("dyeing"), type: "radio" };
  const permRegister = { ...register("perm"), type: "radio" };
  const washHairCycleRegister = { ...register("washHairCycle"), type: "radio" };
  const treatmentProductRegister = { ...register("treatmentProduct"), type: "radio" };

  const dietRegister = { ...register("diet"), type: "radio" };
  const smokingRegister = { ...register("smoking"), type: "radio" };
  const coffeeRegister = { ...register("coffee"), type: "radio" };
  const drinkingRegister = { ...register("drinking"), type: "radio" };
  const workOutRegister = { ...register("workOut"), type: "radio" };
  const sleepTimeRegister = { ...register("sleepTime"), type: "radio" };
  const stressLevelRegister = { ...register("stressLevel"), type: "radio" };

  const { mutate: putUserInquiry, isPending: isPendingPutUserInquiry } = usePutUserInquiry();
  const { mutate: postUserHealthInquiry, isPending: isPendingPostUserHealthInquiry } = usePostUserHealthInquiry();
  const dispatch = useAppDispatch();

  const onSubmit = (event: FormEvent) => {
    handleSubmit(
      async ({
        gender,
        ageGroup,
        hairlossTypeAndLevel,
        hairlossFamilyHistory,
        scalpIssues,
        hairDamageLevel,
        skinColor,
        hairType,
        hairColor,
        diet,
        smoking,
        coffee,
        drinking,
        workOut,
        sleepTime,
        stressLevel,
        dyeing,
        perm,
        preferenceHair,
        preferenceShampoo,
        washHairCycle,
        treatmentProduct,
      }) => {
        if (!userId) {
          throw new Error("`userId` is undefined.");
        }

        if (!gender) {
          throw new Error("`gender` is undefined.");
        }

        if (!ageGroup) {
          throw new Error("`ageGroup` is undefined.");
        }

        if (!hairColor) {
          throw new Error("`hairColor` is undefined.");
        }

        if (!hairType) {
          throw new Error("`hairType` is undefined.");
        }

        const validatedHairlossTypeAndLevel = hairlossTypeAndLevel
          ? await validateZodSchemaFromJSON(hairlossTypeAndLevel, HairlossTypeAndLevelCamelSchema)
          : undefined;
        const hairDamageLevelNum = Number(hairDamageLevel);

        putUserInquiry(
          {
            userId,
            gender,
            ageGroup,
            hairlossType: validatedHairlossTypeAndLevel?.hairlossType,
            hairlossLevel: validatedHairlossTypeAndLevel?.hairlossLevel,
            hairlossFamilyHistory,
            scalpIssues: {
              issue_1: scalpIssues[0] || null,
              issue_2: scalpIssues[1] || null,
              issue_3: scalpIssues[2] || null,
              level_1: scalpIssues[0] ? 2 : 0,
              level_2: scalpIssues[1] ? 2 : 0,
              level_3: scalpIssues[2] ? 2 : 0,
            },
            hairDamageLevel: isNaN(hairDamageLevelNum) ? undefined : hairDamageLevelNum,
            skinColor,
            hairColor,
            hairType,
            lifestyle: {
              dyeing: dyeing,
              perm: perm,
              wash_hair_cycle: washHairCycle,
              treatment_product: treatmentProduct,
            },
            preference: {
              hair_1: preferenceHair,
              shampoo_1: preferenceShampoo[0] || null,
              shampoo_2: preferenceShampoo[1] || null,
            },
          },
          {
            onSuccess: () => {
              postUserHealthInquiry(
                {
                  userId,
                  diet: diet || undefined,
                  smoking: smoking || undefined,
                  coffee: coffee || undefined,
                  drinking: drinking || undefined,
                  work_out: workOut || undefined,
                  sleep_time: sleepTime || undefined,
                  stress_level: stressLevel || undefined,
                },
                {
                  onSuccess: () => {
                    reset();
                    // NOTE: redux로 user data 참조하기 때문에 redux 전역 user state invalidate
                    dispatch(getUsers());
                    queryClient.invalidateQueries({
                      queryKey: [queryKeys.GET_TOP_RANKED_SHAMPOO_LIST],
                    });

                    onSuccess && onSuccess();
                  },
                },
              );
            },
          },
        );
      },
    )(event);
  };

  // side effect: 초기값 지정 및 form 변경 여부 확인
  const { data: account, isPending: isPendingCompany } = useGetAccountInfo();
  const { data: _userInquiry, isPending: isPendingUserInquiry } = useGetUserInquiry({ userId });
  const { data: _userHealthInquiry, isPending: isPendingUserHealthInquiry } = useGetUserHealthInquiry({ userId });

  const userInquiry = _userInquiry?.inquiry;
  const healthInquiry = _userHealthInquiry?.healthInquiry;

  const [initialValuesJSON, setInitialValuesJSON] = useState("");
  const [isFormChanged, setIsFormChanged] = useState(false);

  const currentFormState = watch();

  useEffect(() => {
    if (isPendingCompany || isPendingUserInquiry || isPendingUserHealthInquiry) return;

    const initialScalpIssues = [
      userInquiry?.scalp_issues.issue_1,
      userInquiry?.scalp_issues.issue_2,
      userInquiry?.scalp_issues.issue_3,
    ].filter((item) => item) as BeconUserTypes.ScalpIssueTypeEnum[];

    const initialValues: Form = {
      gender: userInquiry?.gender,
      ageGroup: userInquiry?.age_group || "",
      hairlossTypeAndLevel:
        userInquiry?.hairloss_type && isNonNullish(userInquiry.hairloss_type)
          ? JSON.stringify({
              hairlossType: userInquiry.hairloss_type,
              hairlossLevel: userInquiry.hairloss_level,
            })
          : JSON.stringify({
              hairlossType: BeconUserTypes.HairlossTypeEnum.NONE,
              hairlossLevel: 0,
            }),
      hairlossFamilyHistory: userInquiry?.hairloss_family_history || BeconUserTypes.HairlossFamilyHistoryEnum.NOT_SURE,
      scalpIssues: initialScalpIssues.length > 0 ? initialScalpIssues : [BeconUserTypes.ScalpIssueTypeEnum.NONE],
      hairDamageLevel: userInquiry?.hair_damage_level ? `${userInquiry?.hair_damage_level}` : "0",
      // *정책: 일부 국가는 피부톤, 모발 색, 모발 형태를 선택하지 않음 (기본값: 황색, 흑발, 직모)
      ...(checkHideRaceInquiry(account.data.account.region)
        ? {
            skinColor: BeconUserTypes.SkinColorEnum.YELLOW,
            hairColor: BeconUserTypes.HairColorEnum.BLACK,
            hairType: BeconUserTypes.HairTypeEnum.STRAIGHT,
          }
        : {
            skinColor: userInquiry?.skin_color,
            // * hairColor 및 hairType은 문진을 하지 않아도 기본값을 지녀서 문진을 한 번도 안한 경우를 확인해서 undefined 처리
            hairColor:
              userInquiry?.hair_color === BeconUserTypes.HairColorEnum.BLACK && !userInquiry?.skin_color
                ? undefined
                : userInquiry?.hair_color,
            hairType:
              userInquiry?.hair_type === BeconUserTypes.HairTypeEnum.STRAIGHT && !userInquiry?.skin_color
                ? undefined
                : userInquiry?.hair_type,
          }),
      preferenceShampoo: [
        ...(userInquiry?.preference.shampoo_2 ? [userInquiry?.preference.shampoo_2] : []),
        ...(userInquiry?.preference.shampoo_1 ? [userInquiry?.preference.shampoo_1] : []),
      ],
      preferenceHair: userInquiry?.preference.hair_1 || undefined,
      dyeing: userInquiry?.lifestyle.dyeing || undefined,
      perm: userInquiry?.lifestyle.perm || undefined,
      washHairCycle: userInquiry?.lifestyle.wash_hair_cycle || undefined,
      treatmentProduct: userInquiry?.lifestyle.treatment_product || undefined,
      diet: healthInquiry?.diet,
      smoking: healthInquiry?.smoking,
      coffee: healthInquiry?.coffee,
      drinking: healthInquiry?.drinking,
      workOut: healthInquiry?.work_out,
      sleepTime: healthInquiry?.sleep_time,
      stressLevel: healthInquiry?.stress_level,
    };

    reset(initialValues);

    setInitialValuesJSON(JSON.stringify(initialValues));
  }, [
    account?.region,
    healthInquiry?.coffee,
    healthInquiry?.diet,
    healthInquiry?.drinking,
    healthInquiry?.sleep_time,
    healthInquiry?.smoking,
    healthInquiry?.stress_level,
    healthInquiry?.work_out,
    isPendingCompany,
    isPendingUserHealthInquiry,
    isPendingUserInquiry,
    reset,
    userInquiry?.age_group,
    userInquiry?.gender,
    userInquiry?.hair_color,
    userInquiry?.hair_damage_level,
    userInquiry?.hair_type,
    userInquiry?.hairloss_family_history,
    userInquiry?.hairloss_level,
    userInquiry?.hairloss_type,
    userInquiry?.scalp_issues.issue_1,
    userInquiry?.scalp_issues.issue_2,
    userInquiry?.scalp_issues.issue_3,
    userInquiry?.skin_color,
  ]);

  useEffect(() => {
    setIsFormChanged(JSON.stringify(currentFormState) !== initialValuesJSON);
  }, [currentFormState, initialValuesJSON]);

  return {
    genderOptions,
    ageGroupOptions,
    hairlossTypeAndLevelOptions,
    hairLossFamilyHistoryOptions,
    scalpIssuesOptions,
    hairDamageLevelOptions,
    skinColorOptions,
    hairColorOptions,
    hairTypeOptions,
    preferenceHairOptions,
    preferenceShampooOptions,
    dyeingOptions,
    permOptions,
    washHairCycleOptions,
    treatmentProductOptions,
    dietOptions,
    smokingOptions,
    coffeeOptions,
    drinkingOptions,
    workOutOptions,
    sleepTimeOptions,
    stressLevelOptions,
    genderRegister,
    ageGroupRegister,
    hairlossTypeAndLevelRegister,
    hairlossFamilyHistoryRegister,
    scalpIssuesRegister,
    hairDamageLevelRegister,
    skinColorRegister,
    hairColorRegister,
    hairTypeRegister,
    preferenceHairRegister,
    preferenceShampooRegister,
    dyeingRegister,
    permRegister,
    washHairCycleRegister,
    treatmentProductRegister,
    dietRegister,
    smokingRegister,
    coffeeRegister,
    drinkingRegister,
    workOutRegister,
    sleepTimeRegister,
    stressLevelRegister,
    errorMessages,
    onSubmit,
    isSubmitting: isPendingPutUserInquiry || isPendingPostUserHealthInquiry,
    isFormChanged,
  };
};
